import React, { useEffect, useState, useCallback } from "react";
import { UsersThree, MagnifyingGlass, DownloadSimple, Sparkle, CheckCircle, Users, Ticket, Gift } from "@phosphor-icons/react";
import { PageLoader, EmptyState, Stat } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import api, { API, TOKEN_KEY, apiErr } from "../../lib/api";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { RefreshBar } from "../../components/RefreshBar";

const PAYMENT_STYLES = {
  paid: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25", complimentary: "bg-blue-500/15 text-blue-300 border border-blue-500/25",
  invited: "bg-amber-500/15 text-amber-300 border border-amber-500/25",
};

export default function Attendees() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ event_id: "all", payment: "all", checkin: "all", rsvp: "all", q: "" });
  const [insightQ, setInsightQ] = useState("");
  const [insight, setInsight] = useState(null);
  const [asking, setAsking] = useState(false);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p.set(k, v); });
    return p.toString();
  }, [f]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/organizer/attendees?${params()}`).then((r) => {
      setRows(r.data.attendees); setStats(r.data.stats); setEvents(r.data.events);
    }).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [params]);
  const { refreshing, lastUpdated, refresh } = useAutoRefresh(load, [load]);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const exportAs = async (fmt) => {
    try {
      const p = new URLSearchParams(params()); p.set("format", fmt);
      const res = await fetch(`${API}/organizer/attendees/export?${p.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `eventra-attendees.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}.`);
    } catch (e) { toast.error("Could not export. Please try again."); }
  };

  const ask = async () => {
    if (!insightQ.trim()) return;
    setAsking(true); setInsight(null);
    try {
      const { data } = await api.post("/organizer/attendees/insights", { query: insightQ, event_id: f.event_id !== "all" ? f.event_id : null });
      setInsight(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setAsking(false); }
  };

  return (
    <div>
      <RefreshBar refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
      <div className="overline mb-1">Manage</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Attendees</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={stats.total ?? 0} accent />
        <Stat label="Checked in" value={stats.checked_in ?? 0} />
        <Stat label="Paid" value={stats.paid ?? 0} />
        <Stat label="Attending" value={stats.attending ?? 0} />
      </div>

      {/* AI insights */}
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-6">
        <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Sparkle size={16} weight="fill" className="text-primary" /> Ask AI about your attendees</div>
        <div className="flex gap-2">
          <Input value={insightQ} onChange={(e) => setInsightQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="e.g. Who hasn't checked in? Show me VIPs." className="h-11 bg-background" data-testid="insight-input" />
          <Button onClick={ask} disabled={asking} className="rounded-full h-11 px-5" data-testid="insight-ask">{asking ? "Thinking…" : "Ask"}</Button>
        </div>
        {insight && (
          <div className="mt-3 text-sm" data-testid="insight-answer">
            <p className="text-foreground">{insight.answer}</p>
            {insight.names?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {insight.names.map((n, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-background border border-border">{n}</span>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search name or contact…" className="pl-9 h-10" data-testid="attendee-search" />
        </div>
        <FilterSelect value={f.event_id} onChange={set("event_id")} testid="filter-event" placeholder="All events"
          options={[{ v: "all", l: "All events" }, ...events.map((e) => ({ v: e.id, l: e.title }))]} />
        <FilterSelect value={f.payment} onChange={set("payment")} testid="filter-payment" placeholder="Payment"
          options={[{ v: "all", l: "All payments" }, { v: "paid", l: "Paid" }, { v: "complimentary", l: "Complimentary" }, { v: "invited", l: "Invited (no ticket)" }]} />
        <FilterSelect value={f.checkin} onChange={set("checkin")} testid="filter-checkin" placeholder="Check-in"
          options={[{ v: "all", l: "All" }, { v: "checked_in", l: "Checked in" }, { v: "pending", l: "Not checked in" }]} />
        <FilterSelect value={f.rsvp} onChange={set("rsvp")} testid="filter-rsvp" placeholder="RSVP"
          options={[{ v: "all", l: "All RSVP" }, { v: "attending", l: "Attending" }, { v: "confirmed", l: "Confirmed" }, { v: "maybe", l: "Maybe" }, { v: "not_attending", l: "Not attending" }, { v: "pending", l: "No response" }]} />
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground">{rows.length} result(s)</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => exportAs("csv")} className="rounded-full" data-testid="export-csv"><DownloadSimple size={14} className="mr-1" /> CSV</Button>
          <Button size="sm" variant="outline" onClick={() => exportAs("xlsx")} className="rounded-full" data-testid="export-xlsx"><DownloadSimple size={14} className="mr-1" /> Excel</Button>
          <Button size="sm" variant="outline" onClick={() => exportAs("pdf")} className="rounded-full" data-testid="export-pdf"><DownloadSimple size={14} className="mr-1" /> PDF</Button>
        </div>
      </div>

      {loading ? <PageLoader /> : rows.length === 0 ? (
        <EmptyState icon={UsersThree} title="No attendees found" subtitle="Try changing the filters, or invite guests from the Guests page." testid="no-attendees" />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="attendees-table">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <tr>
                <th className="p-3 font-medium">Name</th><th className="p-3 font-medium">Contact</th>
                <th className="p-3 font-medium">Category</th><th className="p-3 font-medium">Payment</th>
                <th className="p-3 font-medium">RSVP</th><th className="p-3 font-medium">Check-in</th>
                <th className="p-3 font-medium">Event</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`attendee-${r.id}`} className="hover:bg-muted/40">
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3 text-muted-foreground">{r.contact || "—"}</td>
                  <td className="p-3">{r.category}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${PAYMENT_STYLES[r.payment] || "bg-muted"}`}>{r.payment}</span></td>
                  <td className="p-3 capitalize text-muted-foreground">{(r.rsvp || "—").replace(/_/g, " ")}</td>
                  <td className="p-3">{r.checked_in ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-semibold"><CheckCircle size={14} weight="fill" /> In</span> : <span className="text-xs text-muted-foreground">Pending</span>}</td>
                  <td className="p-3 text-muted-foreground truncate max-w-[180px]">{r.event}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10 w-auto min-w-[130px]" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
    </Select>
  );
}
